import { CalendarCellProps } from '@/components/calendar/calendar.types';
import { FixedLeaveTypeId } from '@/services/availability/availability.types';
import { useAvailabilityTypes } from '@/services/availability/useAvailabilityTypes';
import { useLocationService } from '@/services/location/LocationService';
import { FC } from 'react';
import { TeamMemberCalendarContextObject } from '../../team/teamMember/calendar/useTeamMemberCalendarEvents';
import { MyCalendarAvailabilityCell } from './MyCalendarAvailabilityCell';
import { MyCalendarLeaveCell } from './MyCalendarLeaveCell';
import { MyCalendarPatternFallbackCell } from './MyCalendarPatternFallbackCell';
import { MyCalendarShiftsCell } from './MyCalendarShiftsCell';

const hasAvailabilityMismatch = (
  availableTimes: { start: number; end: number }[] | undefined,
  patternStart?: number,
  patternEnd?: number
) => (availableTimes ?? []).some(at => at.start !== patternStart || at.end !== patternEnd);

export const MyCalendarCalendarContent: FC<CalendarCellProps<TeamMemberCalendarContextObject>> = ({
  contextObject
}) => {
  const leaveTypes = useAvailabilityTypes();

  // TODO once we have support for other locations (not selected location), we need to move the location into the contextObject and use it to drive the location marker
  const selectedLocation = useLocationService(state => state.selectedLocation);

  if (!contextObject) return <div />;

  if (contextObject.shifts && contextObject.shifts.length > 0) {
    return <MyCalendarShiftsCell shifts={contextObject.shifts} locationName={selectedLocation?.name} />;
  }

  const leaveLookup = contextObject.leaveType
    ? leaveTypes.find(lt => lt.leaveTypeId === contextObject.leaveType)
    : undefined;
  const unknownLeaveType = contextObject.leaveType === FixedLeaveTypeId.UNKNOWN;

  if (leaveLookup || unknownLeaveType) {
    return <MyCalendarLeaveCell isUnknown={unknownLeaveType} label={leaveLookup?.name} color={leaveLookup?.color} />;
  }

  if (contextObject.availableTimes && contextObject.availableTimes.length > 0) {
    return (
      <MyCalendarAvailabilityCell
        availableTimes={contextObject.availableTimes}
        hasMismatch={hasAvailabilityMismatch(
          contextObject.availableTimes,
          contextObject.patternStart,
          contextObject.patternEnd
        )}
      />
    );
  }

  return (
    <MyCalendarPatternFallbackCell
      patternStart={contextObject.patternStart}
      patternEnd={contextObject.patternEnd}
      hoverOnly={contextObject.hoverOnly}
    />
  );
};
